import {
  type FC,
  type PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { type ModalContentState } from '@common/types/modal';
import cn from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';

import { CompleteContractAnimationProvider } from '@/components/Animations/CompleteContractAnimation/provider';
import { type RootState } from '@/redux/store';

import ArenaOfferPopup from '../ArenaOfferPopup/ArenaOfferPopup';
import { BlitzContractModal } from '../BlitzContractModal/BlitzContractModal';
import { BoostFactory } from '../Boost/BoostFactory';
import { BoostQuarry } from '../Boost/BoostQuarry';
import { BoostWorkshop } from '../Boost/BoostWorkshop';
import { BoostProduction } from '../BoostProduction/BoostProduction';
import BuyArenaOffer from '../BuyArenaOffer/BuyArenaOffer';
import { BuyBingoBallsModal } from '../BuyBingoBallsModal/BuyBingoBallsModal';
import BuyBoostX5X10 from '../BuyBoostX5X10/BuyBoostX5X10';
import { BuyDiceModal } from '../BuyDiceModal/BuyDiceModal';
import BuyMarketBox from '../BuyMarketBox/BuyMarketBox';
import BuyPaidLineModal from '../BuyPaidLineModal/BuyPaidLineModal';
import { BuyScratchTicketModal } from '../BuyScratchTicketModal/BuyScratchTicketModal';
import BuySingleTgems from '../BuySingleTgems/BuySingleTgems';
import BuyUpgradeProductOfDay from '../BuyUpgradeProductOfDay/BuyUpgradeProductOfDay';
import { BuyUsdtModal } from '../BuyUsdtModal/BuyUsdtModal';
import BuyVip from '../BuyVip/BuyVip';
import { ChestInfo } from '../ChestInfo/ChestInfo';
import { CoinDeposit } from '../CoinDeposit/CoinDeposit';
import { CustomContractModal } from '../CustomContractModal/CustomContractModal';
import { EarnTonFromPartners } from '../EarnTonFromPartners/EarnTonFromPartners';
import { FriendsModal } from '../FriendsModal/FriendsModal';
import { GoldenContractModal } from '../GoldenContractModal/GoldenContractModal';
import { GrandContractModal } from '../GrandContractModal/GrandContractModal';
import { LevelUpNotificationModal } from '../Notification/LevelUpNotificationModal/LevelUpNotificationModal';
import OpenNewGoodsModal from '../OpenNewGoods/OpenNewGoodsModal';
import ProductOfDayModal from '../ProductOfDayModal/ProductOfDayModal';
import ProductOfDaySpinModal from '../ProductOfDaySpinModal/ProductOfDaySpinModal';
import { ReferralModal } from '../ReferralModal/ReferralModal';
import { RevenueDetailsModal } from '../RevenueDetailsModal/RevenueDetailsModal';
import SelectVipPriceModal from '../SelectVipPriceModal/SelectVipPriceModal';
import { TakeContract } from '../TakeContract/TakeContract';
import ThanksForPurchase from '../ThanksForPurchase/ThanksForPurchase';
import { TonWalletInfo } from '../TonWalletInfo/TonWalletInfo';
import { WalletEarnDetails } from '../WalletEarnDetails/WalletEarnDetails';
import WelcomeArena from '../WelcomeArena/WelcomeArena';
import { WelcomeBackModal } from '../WelcomeBackModal/WelcomeBackModal';
import WithdrawModal from '../WithdrawModal/WithdrawModal';

import { ModalContext, modalsContentConfig } from './config';

import css from './styles.module.scss';

const Modal: FC<PropsWithChildren> = ({ children }): JSX.Element => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [overlayIsActive, setOverlayIsActive] = useState(false);
  const [isSecondModalOpen, setIsSecondModalOpen] = useState(false);
  const [isThirdModalOpen, setIsThirdModalOpen] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(false);
  // const isFirstVisit = useAppSelector((state) => state.user.isFirstVisit);
  const [modalContent, setModalContent] = useState<ModalContentState>({
    renderKind: 'default',
    props: undefined,
  });
  const errorStatusServer = useSelector(
    (state: RootState) => state.user.serverErrorStatus,
  );

  useEffect(() => {
    if (errorStatusServer) {
      setOverlayIsActive(false);
      closeModal();
    }
  }, [errorStatusServer]);
  const [boostProductionDynamicData, setBooostProductionDynamicData] = useState<
    | {
        inPool: number;
        price: number;
      }
    | undefined
  >();
  const [secondModalContent, setSecondModalContent] =
    useState<ModalContentState>({
      renderKind: 'default',
      props: undefined,
    });
  const [thirdModalContent, setThirdModalContent] = useState<ModalContentState>(
    {
      renderKind: 'default',
      props: undefined,
    },
  );

  const ref = useRef<HTMLDivElement | null>(null);
  const secondRef = useRef<HTMLDivElement | null>(null);
  const thirdRef = useRef<HTMLDivElement | null>(null);
  const modalContentRef = useRef<HTMLDivElement | null>(null);

  const contentConfig = modalsContentConfig[modalContent.renderKind];
  const secondContentConfig =
    modalsContentConfig[secondModalContent.renderKind];
  const thirdContentConfig = modalsContentConfig[thirdModalContent.renderKind];

  const toggleHeaderVisible = (): void => {
    const opened = [
      modalContent.renderKind,
      secondModalContent.renderKind,
      thirdModalContent.renderKind,
    ];
    const headerVisible = opened.some(
      (item) => !!modalsContentConfig[item]?.headerVisible,
    );
    setHeaderVisible(headerVisible);
  };

  const openModal = (modalContent: ModalContentState): void => {
    setModalContent(modalContent);
    setIsOpen(true);
  };

  const openSecondModal = (modalContent: ModalContentState): void => {
    setSecondModalContent(modalContent);
    setIsSecondModalOpen(true);
  };

  const openThirdModal = (modalContent: ModalContentState): void => {
    setThirdModalContent(modalContent);
    setIsThirdModalOpen(true);
  };

  const closeModal = (): void => {
    setIsOpen(false);
    setModalContent({
      renderKind: 'default',
      props: undefined,
    });
  };

  const closeSecondModal = (): void => {
    setIsSecondModalOpen(false);
    setSecondModalContent({
      renderKind: 'default',
      props: undefined,
    });
  };

  const closeThirdModal = (): void => {
    setIsThirdModalOpen(false);
    setThirdModalContent({
      renderKind: 'default',
      props: undefined,
    });
  };

  // const handleClickOutside = (
  //   e: MouseEvent | TouchEvent | FocusEvent,
  // ): void => {
  //   const target = e.target as HTMLElement | null;

  //   if (
  //     !contentConfig?.cancelOutsideClickClose &&
  //     !isFirstVisit &&
  //     !isSecondModalOpen &&
  //     !target?.closest('.helpIcon_block')
  //   ) {
  //     closeModal();
  //   }
  // };

  // const handleClickOutsideSecond = (): void => {
  //   if (!secondContentConfig?.cancelOutsideClickClose && isSecondModalOpen) {
  //     closeSecondModal();
  //   }
  // };

  // useOnClickOutside(ref, handleClickOutside);
  // useOnClickOutside(secondRef, handleClickOutsideSecond);

  const secondModalRef = useRef({
    isOpen: isSecondModalOpen,
    openModal: openSecondModal,
    closeModal: closeSecondModal,
    modalContent: secondModalContent,
  });

  const thirdModalRef = useRef({
    isOpen: isThirdModalOpen,
    openModal: openThirdModal,
    closeModal: closeThirdModal,
    modalContent: thirdModalContent,
  });

  useEffect(() => {
    secondModalRef.current.isOpen = isSecondModalOpen;
    secondModalRef.current.modalContent = secondModalContent;
  }, [
    isSecondModalOpen,
    secondModalContent,
    openSecondModal,
    closeSecondModal,
  ]);

  useEffect(() => {
    thirdModalRef.current.isOpen = isThirdModalOpen;
    thirdModalRef.current.modalContent = thirdModalContent;
  }, [isThirdModalOpen, thirdModalContent, openThirdModal, closeThirdModal]);

  useEffect(() => {
    toggleHeaderVisible();
  }, [modalContent, secondModalContent, thirdModalContent]);

  const contextValue = useMemo(
    () => ({
      isOpen,
      openModal,
      closeModal,
      modalContent,
      boostProductionDynamicData,
      setBooostProductionDynamicData,
      secondModal: secondModalRef.current,
      thirdModal: thirdModalRef.current,
      setOverlayIsActive,
      headerVisible,
    }),
    [
      isOpen,
      openModal,
      closeModal,
      modalContent,
      boostProductionDynamicData,
      setBooostProductionDynamicData,
      setOverlayIsActive,
      headerVisible,
    ],
  );

  return (
    <ModalContext.Provider value={contextValue}>
      <CompleteContractAnimationProvider>
        {children}
        <AnimatePresence>
          {(isOpen ||
            isSecondModalOpen ||
            isThirdModalOpen ||
            overlayIsActive) && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                ease: 'easeIn',
                duration: 0.1,
              }}
              className={cn(
                css.modalWrapper,
                contentConfig?.classNames?.wrapper,
              )}
              style={{
                background:
                  modalContent.renderKind === 'boostFactory'
                    ? 'transparent'
                    : '',
              }}
            >
              {isOpen && (
                <motion.div
                  ref={ref}
                  className={cn(
                    css.modal,
                    contentConfig?.classNames?.modal,
                    isSecondModalOpen && 'absolute',
                  )}
                  initial={contentConfig?.exit ?? { y: '100%' }}
                  animate={contentConfig?.animate ?? { y: 0 }}
                  exit={contentConfig?.exit ?? { y: '100%' }}
                  transition={{
                    ease: 'easeIn',
                    duration: 0.1,
                  }}
                >
                  <div
                    className={contentConfig?.classNames?.content}
                    ref={modalContentRef}
                  >
                    {modalContent.renderKind === 'friendsModal' && (
                      <FriendsModal />
                    )}
                    {modalContent.renderKind === 'claimResources' && (
                      <WelcomeBackModal />
                    )}
                    {modalContent.renderKind === 'referralLevelsTable' && (
                      <ReferralModal />
                    )}
                    {modalContent.renderKind === 'userLevelUp' && (
                      <LevelUpNotificationModal {...modalContent.props} />
                    )}
                    {modalContent.renderKind === 'takeContract' && (
                      <TakeContract {...modalContent.props} />
                    )}
                    {modalContent.renderKind === 'chestInfo' && <ChestInfo />}
                    {modalContent.renderKind === 'boostProduction' && (
                      <BoostProduction {...modalContent.props} />
                    )}
                    {modalContent.renderKind === 'boostFactory' &&
                      modalContent.props.plantType === 'Factory' && (
                        <BoostFactory {...modalContent.props} />
                      )}
                    {modalContent.renderKind === 'boostFactory' &&
                      modalContent.props.plantType === 'Quarry' && (
                        <BoostQuarry {...modalContent.props} />
                      )}
                    {modalContent.renderKind === 'boostFactory' &&
                      modalContent.props.plantType === 'Workshop' && (
                        <BoostWorkshop {...modalContent.props} />
                      )}
                    {modalContent.renderKind === 'openNewGoods' && (
                      <OpenNewGoodsModal {...modalContent.props} />
                    )}
                    {modalContent.renderKind === 'coinDeposit' && (
                      <CoinDeposit {...modalContent.props} />
                    )}
                    {modalContent.renderKind === 'welcomeArena' && (
                      <WelcomeArena />
                    )}
                    {modalContent.renderKind === 'buyMarketBox' && (
                      <BuyMarketBox {...modalContent.props} />
                    )}
                    {modalContent.renderKind === 'tonWalletInfo' && (
                      <TonWalletInfo />
                    )}
                    {modalContent.renderKind === 'buyVip' && (
                      <BuyVip {...modalContent.props} />
                    )}
                    {modalContent.renderKind === 'buySingleTgems' && (
                      <BuySingleTgems {...modalContent.props} />
                    )}
                    {modalContent.renderKind === 'earnTonFromPartners' && (
                      <EarnTonFromPartners />
                    )}
                    {modalContent.renderKind === 'revenueDetails' && (
                      <RevenueDetailsModal />
                    )}
                    {modalContent.renderKind === 'walletEarnDetails' && (
                      <WalletEarnDetails />
                    )}
                    {modalContent.renderKind === 'buyArenaOffer' && (
                      <BuyArenaOffer {...modalContent.props} />
                    )}
                    {modalContent.renderKind === 'arenaOfferPopup' && (
                      <ArenaOfferPopup {...(modalContent.props ?? {})} />
                    )}
                    {modalContent.renderKind === 'withdrawModal' && (
                      <WithdrawModal />
                    )}
                    {modalContent.renderKind === 'selectVipPrice' && (
                      <SelectVipPriceModal {...modalContent.props} />
                    )}
                    {modalContent.renderKind === 'thanksForPurchase' && (
                      <ThanksForPurchase {...modalContent.props} />
                    )}
                    {modalContent.renderKind === 'buyPaidRewardLine' && (
                      <BuyPaidLineModal />
                    )}
                    {modalContent.renderKind === 'buyDiceModal' && (
                      <BuyDiceModal />
                    )}
                    {modalContent.renderKind === 'buyBingoBallsModal' && (
                      <BuyBingoBallsModal source={modalContent.props?.source} />
                    )}
                    {modalContent.renderKind === 'buyScratchTicketModal' && (
                      <BuyScratchTicketModal {...modalContent.props} />
                    )}
                    {modalContent.renderKind === 'grandContractModal' && (
                      <GrandContractModal {...modalContent.props} />
                    )}
                    {modalContent.renderKind === 'goldenContractModal' && (
                      <GoldenContractModal {...modalContent.props} />
                    )}
                    {modalContent.renderKind === 'customContractModal' && (
                      <CustomContractModal {...modalContent.props} />
                    )}
                    {modalContent.renderKind === 'blitzContractModal' && (
                      <BlitzContractModal {...modalContent.props} />
                    )}
                    {modalContent.renderKind === 'productOfDayModal' && (
                      <ProductOfDayModal {...modalContent.props} />
                    )}
                  </div>
                </motion.div>
              )}
              {isSecondModalOpen && (
                <motion.div
                  ref={secondRef}
                  className={cn(
                    css.modal,
                    secondContentConfig?.classNames?.modal,
                    isThirdModalOpen && 'absolute',
                    'isolate',
                  )}
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  exit={{ y: '100%' }}
                  transition={{
                    ease: 'easeIn',
                    duration: 0.1,
                  }}
                >
                  {secondModalContent.renderKind === 'coinDeposit' && (
                    <CoinDeposit {...secondModalContent.props} />
                  )}
                  {secondModalContent.renderKind === 'boostProduction' && (
                    <BoostProduction {...secondModalContent.props} />
                  )}
                  {secondModalContent.renderKind === 'buySingleTgems' && (
                    <BuySingleTgems {...secondModalContent.props} />
                  )}
                  {secondModalContent.renderKind === 'buyBoostX5X10' && (
                    <BuyBoostX5X10 {...secondModalContent.props} />
                  )}
                  {secondModalContent.renderKind === 'buyArenaOffer' && (
                    <BuyArenaOffer {...secondModalContent.props} />
                  )}
                  {secondModalContent.renderKind === 'buyUsdtModal' && (
                    <BuyUsdtModal {...secondModalContent.props} />
                  )}
                  {secondModalContent.renderKind ===
                    'buyProductOfDayUpgrade' && (
                    <BuyUpgradeProductOfDay {...secondModalContent.props} />
                  )}
                  {secondModalContent.renderKind === 'productOfDaySpin' && (
                    <ProductOfDaySpinModal {...secondModalContent.props} />
                  )}
                </motion.div>
              )}
              {isThirdModalOpen && (
                <motion.div
                  ref={thirdRef}
                  className={cn(
                    css.modal,
                    thirdContentConfig?.classNames?.modal,
                    'isolate',
                  )}
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  exit={{ y: '100%' }}
                  transition={{
                    ease: 'easeIn',
                    duration: 0.1,
                  }}
                >
                  {thirdModalContent.renderKind === 'buyUsdtModal' && (
                    <BuyUsdtModal {...thirdModalContent.props} />
                  )}
                  {thirdModalContent.renderKind ===
                    'buyProductOfDayUpgrade' && (
                    <BuyUpgradeProductOfDay {...thirdModalContent.props} />
                  )}
                  {thirdModalContent.renderKind === 'productOfDayModal' && (
                    <ProductOfDayModal {...thirdModalContent.props} />
                  )}
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </CompleteContractAnimationProvider>
    </ModalContext.Provider>
  );
};

export { Modal };
